import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Crown, Loader2, RotateCcw } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { track } from '../lib/analytics';
import { CheckoutShell } from '../components/checkout/CheckoutLayout';
import EmbeddedCheckout from '../components/checkout/EmbeddedCheckout';
import HostedFallback from '../components/checkout/HostedFallback';
import { CheckoutErrorBoundary } from '../components/checkout/CheckoutErrorBoundary';
import OneClickUpgrade from '../components/trial/OneClickUpgrade';

/**
 * Branded checkout (/checkout). Product, live price, Swiggy-style coupons,
 * "Total due today", guarantee + social proof, and the card form embedded via
 * Stripe Elements (Checkout Sessions API). Falls back to Stripe's hosted page
 * when the embedded form isn't available. Admin copy lives in /admin/checkout.
 */
export default function Checkout() {
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  // ?plan=trial → the CA$N trial checkout (Admin → Free vs Trial); everything else is the Pro pass.
  const isTrial = params.get('plan') === 'trial';
  const urlCode = isTrial ? '' : (params.get('code') || '').trim().toUpperCase();
  const [cfg, setCfg] = useState(null);
  const [credit, setCredit] = useState(null);
  const [creditLoaded, setCreditLoaded] = useState(false);
  const [loadErr, setLoadErr] = useState(false);
  const [fallback, setFallback] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [restoreMsg, setRestoreMsg] = useState('');

  const isPro = user?.subscription_status && user.subscription_status !== 'free';
  const trialStatus = user?.trial?.status;

  // Anyone who already paid must never be able to pay twice: silently reconcile
  // with Stripe before showing the payment form.
  const reconcile = useCallback(async (manual) => {
    if (manual) { setRestoring(true); setRestoreMsg(''); }
    try {
      const { data } = await api.post('/api/payments/reconcile');
      if (data?.subscription_status && data.subscription_status !== 'free') {
        localStorage.removeItem('pro_checkout_pending');
        await refreshUser?.();
        return true;
      }
      if (data?.trial && data.trial.status !== 'unpaid' && trialStatus === 'unpaid') {
        await refreshUser?.();
        return true;
      }
      if (manual) setRestoreMsg("We couldn't find a completed payment for this account yet. If your card was charged, it can take a minute — try again shortly or message us.");
    } catch {
      if (manual) setRestoreMsg('Could not check right now. Please try again in a moment.');
    } finally {
      if (manual) setRestoring(false);
    }
    return false;
  }, [refreshUser, trialStatus]);

  const reconciledFor = useRef('');
  useEffect(() => {
    if (user?.id && !isPro && reconciledFor.current !== user.id) {
      reconciledFor.current = user.id;
      reconcile(false);
    }
  }, [user?.id, isPro, reconcile]);

  useEffect(() => {
    let alive = true;
    // The API restarts for ~30s after an Azure settings change — retry before giving up.
    const load = (attempt) => api.get('/api/checkout/config', { params: isTrial ? { plan: 'trial' } : {} })
      .then((r) => {
        if (!alive) return;
        setCfg(r.data);
        api.post('/api/checkout/visit', { code: urlCode, mode: r.data?.embedded_available ? 'embedded' : 'hosted', plan: isTrial ? 'trial' : 'pro' }).catch(() => {});
      })
      .catch(() => {
        if (!alive) return;
        if (attempt < 3) setTimeout(() => load(attempt + 1), 1500 * (attempt + 1));
        else setLoadErr(true);
      });
    load(0);
    track('checkout_view', { code: urlCode || '', plan: isTrial ? 'trial' : 'pro' });
    return () => { alive = false; };
  }, [urlCode, isTrial]);

  useEffect(() => {
    if (!user?.id || isPro || isTrial) { setCredit(null); setCreditLoaded(true); return; }
    setCreditLoaded(false);
    api.get('/api/credits/me').then((r) => setCredit(r.data)).catch(() => setCredit(null)).finally(() => setCreditLoaded(true));
  }, [user?.id, isPro, isTrial]);

  const onFallback = useCallback((reason) => { setFallback(reason || 'unavailable'); track('checkout_fallback', { reason: reason || '' }); }, []);

  if (loadErr) {
    return (
      <CheckoutShell cfg={{}}>
        <div className="max-w-md mx-auto text-center py-16" data-testid="checkout-load-error">
          <p className="font-bold text-slate-900">We couldn't load the checkout.</p>
          <p className="text-sm text-slate-500 mt-1">Our server didn't answer in time. Please try again in a few seconds.</p>
          <div className="flex flex-wrap gap-2 justify-center mt-4">
            <button type="button" onClick={() => { setLoadErr(false); window.location.reload(); }} className="rounded-xl bg-slate-900 text-white text-sm font-semibold px-4 py-2.5" data-testid="checkout-load-retry">Try again</button>
            <Link to="/pricing" className="rounded-xl border border-slate-300 text-slate-700 text-sm font-semibold px-4 py-2.5" data-testid="checkout-load-pricing-link">Back to pricing</Link>
          </div>
        </div>
      </CheckoutShell>
    );
  }
  if (!cfg || !creditLoaded) {
    return (
      <CheckoutShell cfg={cfg || {}}>
        <div className="flex justify-center py-24" data-testid="checkout-loading"><Loader2 size={32} className="animate-spin text-slate-400" /></div>
      </CheckoutShell>
    );
  }

  const isAdmin = user?.role === 'admin';
  // Trial checkout when the trial is off, already running or already used → no second payment.
  if (isTrial && !isAdmin && (isPro || cfg.trial_available === false || (trialStatus && trialStatus !== 'unpaid'))) {
    const active = trialStatus === 'active';
    const title = isPro ? (cfg.already_pro_title || 'You already have Pro access') : active ? cfg.already_active_title : cfg.trial_available === false ? 'The trial is not available right now' : cfg.already_pro_title;
    const text = isPro ? 'Your plan is active — nothing to pay today.' : active ? cfg.already_active_text : cfg.trial_available === false ? 'Create a free account instead, or upgrade to Pro.' : cfg.already_pro_text;
    const ctaTo = (isPro || active || cfg.trial_available === false) ? '/dashboard' : '/checkout';
    const ctaLabel = isPro || active ? (cfg.already_active_cta || 'Go to my dashboard') : cfg.trial_available === false ? 'Go to my dashboard' : (cfg.already_pro_cta || 'Upgrade to Pro');
    return (
      <CheckoutShell cfg={cfg}>
        <SEO title="Checkout" noIndex />
        <div className="max-w-md mx-auto rounded-3xl bg-white border border-slate-200 p-8 text-center" data-testid="trial-already-card" data-state={trialStatus || ''}>
          <div className="w-14 h-14 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-4"><Crown size={28} /></div>
          <h1 className="font-serif text-3xl text-slate-900" data-testid="trial-already-title">{title}</h1>
          <p className="text-sm text-slate-500 mt-2">{text}</p>
          <Link to={ctaTo} className="inline-flex mt-6 rounded-xl bg-slate-900 text-white text-sm font-semibold px-5 py-3" data-testid="trial-already-cta">{ctaLabel}</Link>
        </div>
      </CheckoutShell>
    );
  }
  if (isPro && !isAdmin) {
    return (
      <CheckoutShell cfg={cfg}>
        <SEO title="Checkout" noIndex />
        <div className="max-w-md mx-auto rounded-3xl bg-white border border-slate-200 p-8 text-center" data-testid="already-pro-card">
          <div className="w-14 h-14 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-4"><Crown size={28} /></div>
          <h1 className="font-serif text-3xl text-slate-900" data-testid="already-pro-title">{cfg.already_pro_title || 'You already have Pro access'}</h1>
          <p className="text-sm text-slate-500 mt-2">{cfg.already_pro_text || "Your plan is active — there's nothing to pay today."}</p>
          <Link to="/dashboard" className="inline-flex mt-6 rounded-xl bg-slate-900 text-white text-sm font-semibold px-5 py-3" data-testid="already-pro-dashboard-link">{cfg.already_pro_cta || 'Go to my dashboard'}</Link>
        </div>
      </CheckoutShell>
    );
  }

  const banner = isAdmin ? (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-4">
      <div className="rounded-xl bg-slate-900 text-white text-xs font-semibold px-4 py-2 text-center" data-testid="admin-preview-banner">
        Admin preview — students see this exact page.{isPro ? ' You already have Pro, so nothing will be charged unless you pay.' : ''} Edit it in Admin → Checkout Page.
      </div>
    </div>
  ) : null;

  const useEmbedded = cfg.embedded_available && !fallback;
  const cfgWithCredit = { ...cfg, credit: credit || undefined, plan: isTrial ? 'trial' : 'pro' };
  return (
    <CheckoutShell cfg={cfg} banner={banner}>
      <SEO title="Secure Checkout" noIndex />
      {!isTrial && !isPro && <OneClickUpgrade variant="banner" />}
      {useEmbedded
        ? (
          <CheckoutErrorBoundary onCrash={() => onFallback('client_crash')}
            fallback={<HostedFallback cfg={cfgWithCredit} urlCode={urlCode} email={user?.email} reason="client_crash" />}>
            <EmbeddedCheckout cfg={cfgWithCredit} urlCode={urlCode} email={user?.email} onFallback={onFallback} />
          </CheckoutErrorBoundary>
        )
        : <HostedFallback cfg={cfgWithCredit} urlCode={urlCode} email={user?.email} reason={fallback || 'not_configured'} />}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-8 text-center" data-testid="restore-purchase-row">
        {isTrial && (
          <p className="text-xs text-slate-500 mb-3" data-testid="trial-checkout-pro-hint">
            Want everything right away? <Link to="/checkout" className="font-semibold text-slate-700 underline underline-offset-2" data-testid="trial-checkout-pro-link">Go straight to Pro</Link>
          </p>
        )}
        <button type="button" onClick={() => reconcile(true)} disabled={restoring}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 disabled:opacity-50 transition-colors" data-testid="restore-purchase-btn">
          {restoring ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
          {isTrial ? 'Already paid for the trial but still locked? Restore my payment' : (cfg.restore_purchase_label || 'Already paid but still not Pro? Restore my purchase')}
        </button>
        {restoreMsg && <p className="text-xs text-slate-500 mt-2 max-w-md mx-auto" data-testid="restore-purchase-msg">{restoreMsg}</p>}
      </div>
    </CheckoutShell>
  );
}
